import { useEffect, useMemo, useState } from 'react';

export const THEMES = {
    dark: {
        name: 'dark',
        bg: '#0f172a', bgAlt: '#0b1220', surface: '#1e293b', surfaceHover: '#273549',
        border: '#1e293b', borderLight: '#334155',
        text: '#f1f5f9', textSub: '#94a3b8', textMuted: '#64748b',
        accent: '#2563eb', accentSoft: 'rgba(37,99,235,0.14)', accentText: '#fff',
        danger: '#ef4444', dangerSoft: 'rgba(239,68,68,0.12)', success: '#22c55e', warning: '#f59e0b',
        viewportBg: '#0b1220', grid: '#1e293b', gridSection: '#334155', fog: '#0b1220',
        inputBg: '#0f172a', overlay: 'rgba(2,6,23,0.82)',
        shadow: '0 20px 60px rgba(0,0,0,0.55)',
        segActiveBg: '#334155', segActiveText: '#f1f5f9',
    },
    light: {
        name: 'light',
        bg: '#f1f5f9', bgAlt: '#e2e8f0', surface: '#ffffff', surfaceHover: '#f0f6ff',
        border: '#e2e8f0', borderLight: '#cbd5e1',
        text: '#0f172a', textSub: '#475569', textMuted: '#94a3b8',
        accent: '#2563eb', accentSoft: 'rgba(37,99,235,0.10)', accentText: '#fff',
        danger: '#dc2626', dangerSoft: 'rgba(220,38,38,0.08)', success: '#16a34a', warning: '#d97706',
        viewportBg: '#e8eef7', grid: '#cbd5e1', gridSection: '#94a3b8', fog: '#e8eef7',
        inputBg: '#f8fafc', overlay: 'rgba(15,23,42,0.45)',
        shadow: '0 20px 60px rgba(15,23,42,0.18)',
        segActiveBg: '#dbeafe', segActiveText: '#1e40af',
    },
};

export function resolveTheme(preference) {
    if (preference === 'light' || preference === 'dark') return preference;
    if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) return 'light';
    return 'dark';
}

/** Hook returning the active theme token set, following light/dark/system. */
export function useThemeTokens() {
    const theme = usePrefStore();
    const [systemDark, setSystemDark] = useState(() => !(typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: light)').matches));
    useEffect(() => {
        if (theme !== 'system' || !window.matchMedia) return;
        const mq = window.matchMedia('(prefers-color-scheme: light)');
        const fn = (e) => setSystemDark(!e.matches);
        mq.addEventListener?.('change', fn);
        return () => mq.removeEventListener?.('change', fn);
    }, [theme]);
    const resolved = theme === 'system' ? (systemDark ? 'dark' : 'light') : theme;
    return useMemo(() => THEMES[resolved] || THEMES.dark, [resolved]);
}

// tiny standalone store for theme preference to avoid circular imports
let _pref = 'dark';
try { _pref = JSON.parse(localStorage.getItem('roocad_settings'))?.theme || 'dark'; } catch { }
const _listeners = new Set();
export function setThemePreference(t) {
    _pref = t;
    try { const s = JSON.parse(localStorage.getItem('roocad_settings') || '{}'); s.theme = t; localStorage.setItem('roocad_settings', JSON.stringify(s)); } catch { }
    _listeners.forEach((l) => l());
}
function usePrefStore() {
    const [, force] = useState(0);
    useEffect(() => { const l = () => force((n) => n + 1); _listeners.add(l); return () => _listeners.delete(l); }, []);
    return _pref;
}